import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { getAppIcon, getAppColor, getAppLetter, formatDate } from "@/lib/utils";
import { Application } from "@shared/schema";

type ViewApplicationDialogProps = {
  applicationId: number | null;
  isOpen: boolean;
  onClose: () => void;
};

export default function ViewApplicationDialog({
  applicationId,
  isOpen,
  onClose,
}: ViewApplicationDialogProps) {
  // Query the application data when the dialog is open and an ID is provided
  const { data: application, isLoading } = useQuery<Application>({
    queryKey: ['/api/applications', applicationId],
    queryFn: async () => {
      if (!applicationId) throw new Error("Application ID is required");
      const res = await fetch(`/api/applications/${applicationId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch application');
      }
      return res.json();
    },
    enabled: isOpen && applicationId !== null,
  });

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Application Details</DialogTitle>
          <DialogDescription>
            View details for this application
          </DialogDescription>
        </DialogHeader>

        {isLoading || !application ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <div className="mt-4 space-y-6">
            <div className="flex items-start gap-4">
              <div className={`h-16 w-16 rounded ${getAppColor(application.name)} flex items-center justify-center flex-shrink-0`}>
                <span className="text-2xl font-bold">{getAppLetter(application.name)}</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-1">{application.name}</h3>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline">{application.category}</Badge>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Added On</h4>
                  <p>{formatDate(application.addedAt)}</p>
                </div>
                {application.description && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Description</h4>
                    <p className="text-sm">{application.description}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}